import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Bell, Sun, Moon, Globe, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import { api } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";
import { Logo } from "@/components/brand/Logo";

type Child = { id: string; first_name: string; last_name: string };

export type PortalCtx = {
    children: (Child & { is_payer?: boolean })[];
    activeChildId: string | null;
    setActiveChildId: (id: string) => void;
    tenant: { name: string; language: string; currency: string; timezone: string } | null;
    isPayer: boolean;
    payerChildIds: string[];
};

// A tiny context via URL query or localStorage. We use localStorage — the portal is single-user.
const ACTIVE_CHILD_KEY = "eduerp.portal.active_child";

export default function PortalLayout() {
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const { me, activeMembership, logout } = useAuth();
    const navigate = useNavigate();
    const [meData, setMeData] = useState<any>(null);
    const [activeChildId, setActiveChildIdState] = useState<string | null>(null);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifs, setNotifs] = useState<any[]>([]);
    const [unread, setUnread] = useState(0);

    const setActiveChildId = (id: string) => {
        localStorage.setItem(ACTIVE_CHILD_KEY, id);
        setActiveChildIdState(id);
    };

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/portal/me");
                setMeData(data);
                const kids: Child[] = data.children || [];
                const stored = localStorage.getItem(ACTIVE_CHILD_KEY);
                const pick = kids.find((c) => c.id === stored) ?? kids[0];
                if (pick) setActiveChildIdState(pick.id);
            } catch {
                setMeData({ children: [] });
            }
        })();
    }, []);

    const loadNotifs = async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 20 } });
            setNotifs(data.items || []);
            setUnread(data.unread_count || 0);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 30000); return () => clearInterval(t); }, []);

    const cycleLang = () => {
        const order = ["az", "en", "ru"] as const;
        const cur = i18n.language.startsWith("ru") ? "ru" : i18n.language.startsWith("en") ? "en" : "az";
        i18n.changeLanguage(order[(order.indexOf(cur) + 1) % order.length]);
    };
    const currentLangLabel = i18n.language.startsWith("ru") ? "RU" : i18n.language.startsWith("en") ? "EN" : "AZ";

    const markRead = async (id: string) => {
        try { await api.post(`/notifications/${id}/read`); await loadNotifs(); } catch { /* ignore */ }
    };
    const markAllRead = async () => {
        try { await api.post("/notifications/mark-all-read"); await loadNotifs(); } catch { /* ignore */ }
    };

    // Portal has a distinct, calmer look: warm background, no sidebar, generous spacing.
    return (
        <div className="min-h-screen" style={{ background: "var(--page)", color: "var(--text)" }}>
            <header className="sticky top-0 z-30 border-b" style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="portal-topbar">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center gap-3" style={{ height: 64 }}>
                    <div className="flex items-center gap-2">
                        <Logo variant="icon" height={32} testId="mobile-brand-mark" />
                        <div className="hidden sm:flex flex-col leading-tight">
                            <span className="text-sm font-semibold tracking-tight">{meData?.is_payer ? t("portal.title.payer") : t("portal.title")}</span>
                            <span className="text-xs" style={{ color: "var(--text-muted)" }}>{meData?.tenant?.name || ""}</span>
                        </div>
                    </div>
                    <div className="flex-1" />
                    {/* Notification bell */}
                    <div className="relative">
                        <button data-testid="portal-bell" onClick={() => setNotifOpen((v) => !v)} className="inline-flex items-center justify-center rounded-md border relative" style={{ width: 40, height: 40, color: "var(--text-muted)", background: "var(--input)", borderColor: "var(--border)" }} aria-label={t("topbar.notifications") as string}>
                            <Bell size={18} />
                            {unread > 0 && (
                                <span className="absolute -top-1 -right-1 rounded-full text-[10px] font-bold px-1.5 py-0.5 leading-none" style={{ background: "var(--error-fg)", color: "#fff", minWidth: 18, textAlign: "center" }} data-testid="portal-bell-badge">{unread}</span>
                            )}
                        </button>
                        {notifOpen && (
                            <div data-testid="portal-bell-menu" className="absolute right-0 top-11 z-40 rounded-md border shadow-xl w-[340px] max-w-[calc(100vw-32px)]" style={{ background: "var(--menu)", borderColor: "var(--border)" }}>
                                <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "var(--border)" }}>
                                    <span className="text-sm font-medium">{t("notif.title")}</span>
                                    {unread > 0 && <button onClick={markAllRead} className="text-xs" style={{ color: "var(--primary)" }} data-testid="mark-all-read">{t("notif.mark_all_read")}</button>}
                                </div>
                                <div className="max-h-[400px] overflow-y-auto">
                                    {notifs.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--text-muted)" }}>{t("notif.empty")}</div>}
                                    {notifs.map((n) => (
                                        <button key={n.id} onClick={() => markRead(n.id)} className="w-full text-left px-3 py-2 border-b hover:bg-[var(--selected-bg)] transition-colors" style={{ borderColor: "var(--border)" }} data-testid={`notif-item-${n.id}`}>
                                            <div className="flex items-start gap-2">
                                                {n.status === "unread" && <span className="mt-1.5 rounded-full" style={{ width: 8, height: 8, background: "var(--primary)" }} />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate">{String(t(n.title_key, n.params || {}))}</div>
                                                    <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{String(t(n.body_key, n.params || {}))}</div>
                                                    <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{fmtDateTime(n.created_at)}</div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <button data-testid="portal-lang" onClick={cycleLang} className="inline-flex items-center rounded-md px-2.5 text-xs font-medium border uppercase" style={{ minHeight: 40, minWidth: 44, color: "var(--text)", background: "var(--input)", borderColor: "var(--border)" }}>{currentLangLabel}</button>
                    <button data-testid="portal-theme" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="inline-flex items-center rounded-md px-2.5 border" style={{ minHeight: 40, minWidth: 44, color: "var(--text)", background: "var(--input)", borderColor: "var(--border)" }}>{theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}</button>
                    <button data-testid="portal-logout" onClick={async () => { await logout(); navigate("/login", { replace: true }); }} className="inline-flex items-center gap-1 rounded-md px-3 text-sm border" style={{ minHeight: 40, color: "var(--text-muted)", background: "var(--input)", borderColor: "var(--border)" }}>
                        <LogOut size={16} />
                    </button>
                </div>
                {/* Child switcher row (only if there are children) */}
                {meData?.children?.length > 0 && (
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-3 flex items-center gap-2 overflow-x-auto" data-testid="portal-child-switcher">
                        <span className="text-xs shrink-0" style={{ color: "var(--text-muted)" }}>{t("portal.children")}</span>
                        {meData.children.map((c: Child) => {
                            const active = c.id === activeChildId;
                            return (
                                <button key={c.id} data-testid={`portal-child-${c.id}`} onClick={() => setActiveChildId(c.id)} className="rounded-full px-3 py-1.5 text-sm border shrink-0 transition-colors" style={{ background: active ? "var(--primary)" : "var(--input)", color: active ? "var(--primary-fg)" : "var(--text)", borderColor: active ? "var(--primary)" : "var(--border)" }}>
                                    {c.first_name} {c.last_name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
                <Outlet context={{
                    children: meData?.children || [],
                    activeChildId, setActiveChildId,
                    tenant: meData?.tenant || null,
                    isPayer: !!meData?.is_payer,
                    payerChildIds: meData?.payer_child_ids || [],
                } as PortalCtx} />
            </main>
        </div>
    );
}
